import React from 'react';
import cn from 'classnames';
import { MagnifyingGlassIcon } from '@phosphor-icons/react';

export interface MPFComboboxOption {
  id: string;
  content: React.ReactNode;
  meta?: React.ReactNode;
}

export interface MPFComboboxProps {
  label: string;
  hideLabel?: boolean;
  placeholder?: string;
  value: string;
  onValueChange: (value: string) => void;
  options: MPFComboboxOption[];
  onSelect: (option: MPFComboboxOption) => void;
  onSubmit: (value: string) => void;
  footer?: React.ReactNode;
  emptyContent?: React.ReactNode;
  statusText?: string;
  className?: string;
  submitIcon?: React.ReactNode;
  submitLabel?: string;
}

export const MPFCombobox: React.FC<MPFComboboxProps> = ({
  label,
  hideLabel,
  placeholder,
  value,
  onValueChange,
  options,
  onSelect,
  onSubmit,
  footer,
  emptyContent,
  statusText,
  className,
  submitIcon,
  submitLabel = 'Buscar',
}) => {
  const baseId = React.useId();
  const inputId = `${baseId}-input`;
  const listboxId = `${baseId}-listbox`;
  const [aberto, setAberto] = React.useState(false);
  const [ativo, setAtivo] = React.useState(-1);
  const raizRef = React.useRef<HTMLDivElement>(null);

  const expandido = aberto && options.length > 0;
  const vazio = aberto && options.length === 0 && Boolean(emptyContent);
  const idDaOpcao = (index: number) => `${baseId}-opcao-${index}`;

  React.useEffect(() => {
    setAtivo(-1);
  }, [options]);

  React.useEffect(() => {
    if (ativo < 0) return;
    document
      .getElementById(`${baseId}-opcao-${ativo}`)
      ?.scrollIntoView?.({ block: 'nearest' });
  }, [ativo, baseId]);

  const escolher = (index: number) => {
    const option = options[index];
    if (!option) return;
    setAberto(false);
    onSelect(option);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!expandido) {
          setAberto(true);
          return;
        }
        setAtivo((i) => (i + 1) % options.length);
        return;
      case 'ArrowUp':
        event.preventDefault();
        if (!expandido) {
          setAberto(true);
          return;
        }
        setAtivo((i) => (i <= 0 ? options.length - 1 : i - 1));
        return;
      case 'Enter':
        event.preventDefault();
        if (expandido && ativo >= 0) {
          escolher(ativo);
        } else {
          setAberto(true);
          onSubmit(value);
        }
        return;
      case 'Escape':
        if (expandido) {
          event.preventDefault();
          setAberto(false);
          setAtivo(-1);
        } else if (value) {
          event.preventDefault();
          onValueChange('');
        }
        return;
      default:
    }
  };

  const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    if (!raizRef.current?.contains(event.relatedTarget as Node)) {
      setAberto(false);
    }
  };

  return (
    <div
      ref={raizRef}
      className={cn('mpf-combobox', className)}
      onBlur={handleBlur}
    >
      <label
        htmlFor={inputId}
        className={cn('mpf-combobox__rotulo', { 'sr-only': hideLabel })}
      >
        {label}
      </label>
      <div className="mpf-combobox__campo">
        <input
          id={inputId}
          type="search"
          className="mpf-combobox__input"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={expandido}
          aria-controls={listboxId}
          aria-activedescendant={
            expandido && ativo >= 0 ? idDaOpcao(ativo) : undefined
          }
          autoComplete="off"
          placeholder={placeholder}
          value={value}
          onChange={(event) => {
            onValueChange(event.target.value);
            setAberto(true);
          }}
          onFocus={() => setAberto(true)}
          onKeyDown={handleKeyDown}
        />
        <button
          type="submit"
          className="mpf-combobox__enviar"
          aria-label={submitLabel}
          onClick={(event) => {
            event.preventDefault();
            setAberto(true);
            onSubmit(value);
          }}
        >
          {submitIcon ?? <MagnifyingGlassIcon aria-hidden="true" />}
        </button>
      </div>

      <div className="mpf-combobox__painel" hidden={!expandido && !vazio}>
        <ul
          id={listboxId}
          role="listbox"
          aria-label={label}
          className="mpf-combobox__lista"
        >
          {options.map((option, index) => (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events -- no padrão combobox o teclado fica no <input> (setas + Enter, em `handleKeyDown`); a opção nunca recebe foco.
            <li
              key={option.id}
              id={idDaOpcao(index)}
              role="option"
              aria-selected={index === ativo}
              className={cn('mpf-combobox__opcao', {
                'mpf-combobox__opcao--ativa': index === ativo,
              })}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => escolher(index)}
              onMouseEnter={() => setAtivo(index)}
            >
              <span className="mpf-combobox__opcao-texto">
                {option.content}
              </span>
              {option.meta && (
                <span className="mpf-combobox__opcao-meta">{option.meta}</span>
              )}
            </li>
          ))}
        </ul>
        {vazio && <div className="mpf-combobox__vazio">{emptyContent}</div>}
        {expandido && footer && (
          <div className="mpf-combobox__rodape">{footer}</div>
        )}
      </div>

      <div role="status" aria-live="polite" className="sr-only">
        {aberto ? statusText : ''}
      </div>
    </div>
  );
};

export default MPFCombobox;
